import { LandingMediaDropbox } from './MediaDropBox';
import { faCheck, faImage } from '@fortawesome/pro-regular-svg-icons';
import { IFeaturesNewSection } from '@playbook-technologies/common-schemas';
import Select from 'react-select';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { sortBy } from 'lodash';

type Option = {
  value: string;
};
type Feature = IFeaturesNewSection['features'][0];

const ALL_FEATURES: Feature[] = [
  {
    type: 'reminders',
    name: 'Reminders',
    image: '/images/landing/features/reminders.png',
  },
  {
    type: 'streaks',
    name: 'Streaks',
    image: '/images/landing/features/streaks.png',
  },
  {
    type: 'tracking',
    name: 'Tracking',
    image: '/images/landing/features/tracking.png',
  },
  {
    type: 'community',
    name: 'Community',
    image: '/images/landing/features/community.png',
  },
  {
    type: 'favorites',
    name: 'Favorites',
    image: '/images/landing/features/favorites.png',
  },
  {
    type: 'notes',
    name: 'Notes & Logging',
    image: '/images/landing/features/notes.png',
  },
  {
    type: 'videos',
    name: 'Video Workouts',
    image: '/images/landing/features/videos.png',
  },
  {
    type: 'stream',
    name: 'Stream Anywhere',
    image: '/images/landing/features/stream.png',
  },
  {
    type: 'offline',
    name: 'Offline Downloads',
    image: '/images/landing/features/offline.png',
  },
  {
    type: 'playlist',
    name: 'Curated Playlists',
    image: '/images/landing/features/playlist.png',
  },
  {
    type: 'nutrition',
    name: 'Nutrition',
    image: '/images/landing/features/nutrition.png',
  },
  {
    type: 'messages',
    name: 'DMs',
    image: '/images/landing/features/messaging.png',
  },
];

export const DEFAULT_FEATURES = ALL_FEATURES.slice(0, 6);

export type FeaturesNewSectionProps = {
  imageUpload: (file: File) => Promise<string>;
  value: IFeaturesNewSection;
  onChange: (x: IFeaturesNewSection) => void;
};
export function FeaturesNewSection({
  imageUpload,
  value,
  onChange,
}: FeaturesNewSectionProps) {
  function mergeState(x: Partial<IFeaturesNewSection>) {
    onChange({ ...value, ...x });
  }

  const landingPage = import.meta.env.VITE_LANDING_PAGE_URL;
  const features = value.features;

  const selectedTypes = features.map((f: Feature) => f.type);
  const options = sortBy(ALL_FEATURES, (f) => [
    selectedTypes.includes(f.type),
    f.name,
  ]).map((f: Feature) => ({
    label: (
      <span>
        {f.name}{' '}
        {selectedTypes.includes(f.type) ? <Icon icon={faCheck} /> : null}
      </span>
    ),
    value: f.type,
  }));

  const handleCoverChange = (index: number) => (coverImage: string) =>
    mergeState({
      features: features.map((f: Feature, i: number) => {
        if (i === index) {
          return { ...f, image: coverImage };
        } else {
          return f;
        }
      }),
    });

  const handleSelectChange = (index: number) => (option: Option | null) =>
    mergeState({
      features: features.map((f: Feature, i: number) => {
        if (i === index) {
          return ALL_FEATURES.find((f) => f.type === option?.value) as Feature; // NOTE: assumes that find succeeds
        } else {
          return f;
        }
      }),
    });

  return (
    <div data-testid="features-new-section">
      <div className="flex flex-wrap md:grid md:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-20">
        {features.map((f, i) => {
          return (
            <div key={i} data-testid={`feature-item-${i}`}>
              <Select
                className="w-full mb-3"
                options={options}
                value={options.find((o: any) => o.value == f.type)}
                onChange={handleSelectChange(i)}
              />
              <LandingMediaDropbox
                width="127px"
                height="260px"
                upload={imageUpload}
                label={'Image'}
                validationText=""
                value={
                  f.image?.startsWith('/')
                    ? `${landingPage}${f.image}`
                    : f.image
                }
                onChange={handleCoverChange(i)}
                type="image"
                icon={faImage as any}
              >
                <ul className="text-black list-none text-center text-h12">
                  <li>Iphone resolution</li>
                </ul>
              </LandingMediaDropbox>
            </div>
          );
        })}
      </div>
    </div>
  );
}
